import { dispatchRemoteControl } from './calls/controlEvents';
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { api } from '../api/client';
import { setUploadChatTransport } from '../api/uploadConcurrency';
import type {
  Attachment,
  ChatActionAck,
  ChatFolder,
  ForwardTarget,
  Dialog,
  DialogEvent,
  DialogSummaryWire,
  DialogWire,
  HistoryAck,
  User,
} from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useChatNotifications, type NotificationReport } from './useChatNotifications';
import { messageNotificationSource, type TaskNotifications } from './taskNotifications';
import type { CallInfo, CallListener, CallReply } from './calls/types';

type ChatNotificationPermission = NotificationPermission | 'unsupported';

interface ChatContextValue {
  callsAvailable: boolean;
  callRequest: (event: string, payload: unknown) => Promise<CallReply>;
  subscribeCalls: (listener: CallListener) => () => void;
  connected: boolean;
  messageActionsAvailable: boolean;
  messengerAvailable: boolean;
  folders: ChatFolder[];
  messengerAction: (name: string, payload: unknown) => Promise<ChatActionAck>;
  contacts: User[];
  dialogs: Dialog[];
  usersById: Record<number, User>;
  unreadCount: number;
  assetMessages: Record<number, DialogEvent[]>;
  assetHistory: Record<number, { loading: boolean; exhausted: boolean }>;
  notificationPermission: ChatNotificationPermission;
  enableNotifications: () => Promise<void>;
  notificationReport: NotificationReport;
  notificationSoundVolume: number;
  notificationSoundMuted: boolean;
  taskNotifications: TaskNotifications;
  setTaskNotificationEnabled: (channel: keyof TaskNotifications, enabled: boolean) => void;
  setNotificationSoundVolume: (volume:number)=>void;
  toggleNotificationSound: ()=>void;
  testNotificationSound: () => Promise<void>;
  testNotificationPopup: () => void;
  createDialog: (title: string, participants: number[]) => void;
  invite: (dialogId: string, participants: number[]) => void;
  leave: (dialogId: string) => void;
  rename: (dialogId: string, title: string) => void;
  loadDialog: (dialogId: string) => void;
  loadOlderDialogEvents: (dialogId: string) => void;
  markSeen: (dialogId: string, eventId: string) => void;
  markAllSeen: () => Promise<void>;
  replyToDialog: (dialogId: string, replyToId: string, text: string, assetId?: number, files?: Attachment[]) => Promise<ChatActionAck>;
  forwardMessage: (messageId: string, target: ForwardTarget) => Promise<ChatActionAck>;
  sendToDialog: (dialogId: string, text: string, assetId?: number, files?: Attachment[]) => void;
  sendDirect: (userId: number, text: string, assetId?: number, files?: Attachment[]) => void;
  joinAsset: (assetId: number) => void;
  leaveAsset: (assetId: number) => void;
  loadOlderAssetMessages: (assetId: number) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

const eventDate = (event: DialogEvent) => new Date(event.created_at).valueOf();

function mergeEvents(current: DialogEvent[], incoming: DialogEvent[]): DialogEvent[] {
  const merged = new Map(current.map((event) => [event._id, event]));
  incoming.forEach((event) => merged.set(event._id, { ...merged.get(event._id), ...event }));
  return [...merged.values()].sort((a, b) => eventDate(a) - eventDate(b));
}

function filesForWire(files: Attachment[] | undefined) {
  return files?.map((file) => ({
    id: file.id,
    attachmentUrl: file.attachmentUrl,
    previewUrl: file.previewUrl,
    name: file.name,
  }));
}

function plainMessage(html: string | undefined): string {
  const container = document.createElement('div');
  container.innerHTML = html ?? '';
  return (container.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 180);
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user, refresh } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const callListeners = useRef(new Set<CallListener>());
  const [callsAvailable, setCallsAvailable] = useState(false);
  const subscribeCalls = useCallback((listener: CallListener) => {
    callListeners.current.add(listener);
    return () => { callListeners.current.delete(listener); };
  }, []);
  const callRequest = useCallback((event: string, payload: unknown) => new Promise<CallReply>((resolve, reject) => {
    const socket = socketRef.current;
    if (!socket?.connected) { reject(new Error('Нет соединения с чатом.')); return; }
    socket.timeout(15000).emit(event, payload, (error: Error | null, result?: CallReply) => {
      if (error) reject(new Error('Сервер звонков не ответил. Повторите попытку.'));
      else if (!result?.successful) reject(new Error(result?.error || 'Не удалось подключить звонок.'));
      else resolve(result);
    });
  }), []);
  const usersRef = useRef<Record<number, User>>({});
  const dialogsRef = useRef<Record<string, Dialog>>({});
  const deliveredIds = useRef(new Set<string>());
  const receivedLiveIds = useRef(new Set<string>());
  const recoverySince = useRef<number | null>(null);
  const notifications = useChatNotifications(user?.id);
  const { notify, permission: notificationPermission, enable: enableNotifications, report: notificationReport, testSound: testNotificationSound, testPopup: testNotificationPopup } = notifications;
  const {volume:notificationSoundVolume,muted:notificationSoundMuted,setVolume:setNotificationSoundVolume,toggle:toggleNotificationSound}=notifications.sound;
  const {sound:taskSound,popup:taskPopup,setEnabled:setTaskNotificationEnabled}=notifications.tasks;
  const [connected, setConnected] = useState(false);
  const [messageActionsAvailable, setMessageActionsAvailable] = useState(false);
  const [messengerAvailable, setMessengerAvailable] = useState(false);
  const [folders, setFolders] = useState<ChatFolder[]>([]);
  const [usersById, setUsersById] = useState<Record<number, User>>({});
  const [dialogsById, setDialogsById] = useState<Record<string, Dialog>>({});
  const [assetMessages, setAssetMessages] = useState<Record<number, DialogEvent[]>>({});
  const [assetHistory, setAssetHistory] = useState<Record<number, { loading: boolean; exhausted: boolean }>>({});
  const setDialogs = useCallback((updater: (dialogs: Record<string, Dialog>) => Record<string, Dialog>) => {
    setDialogsById((current) => {
      const next = updater(current);
      dialogsRef.current = next;
      return next;
    });
  }, []);

  const decorateEvent = useCallback(
    (event: DialogEvent): DialogEvent => ({
      ...event,
      seen: event.receivers?.find((receiver) => receiver.id === user?.id)?.seen ?? true,
    }),
    [user?.id],
  );

  const decorateDialog = useCallback(
    (wire: DialogWire, summary?: Partial<Dialog>): Dialog => {
      const otherId = wire.direct ? wire.participants.find((id) => id !== user?.id) : undefined;
      const other = otherId === undefined ? undefined : usersRef.current[otherId];
      return {
        id: wire._id,
        system: wire.system,
        title: other?.initial ?? wire.title ?? 'Диалог',
        icon: other?.avatar ?? wire.icon,
        direct: wire.direct,
        participants: wire.participants,
        active: wire.direct ? other?.active ?? false : true,
        updated: summary?.updated ?? null,
        updatesCount: summary?.updatesCount ?? 0,
        events: summary?.events ?? [],
        loaded: summary?.loaded ?? false,
        loadingHistory: summary?.loadingHistory ?? false,
        historyExhausted: summary?.historyExhausted ?? false,
      };
    },
    [user?.id],
  );

  useEffect(() => {
    if (!user?.token) return;
    const socket = io('/', {
      autoConnect: false,
      multiplex: false,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.5,
    });
    socketRef.current = socket;
    let authRetry: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const authenticate = async () => {
      try {
        const users = await api.chatUsers();
        const directory = Object.fromEntries(users.map((item) => [item.id, item]));
        usersRef.current = directory;
        setUsersById(directory);
        if (stopped || !socket.connected) return;
        socket.timeout(10000).emit('auth', user.token, { protocol: 2 }, (error: Error | null, successful?: boolean) => {
          if (stopped) return;
          setConnected(!error && successful === true);
          if (error) authRetry = setTimeout(() => { if (socket.connected) void authenticate(); }, 3000);
          else if (!successful) void refresh();
        });
      } catch {
        setConnected(false);
        if (!stopped) authRetry = setTimeout(() => { if (socket.connected) void authenticate(); }, 3000);
      }
    };

    socket.on('connect', () => {
      setUploadChatTransport(socket.io.engine.transport.name);
      socket.io.engine.once('upgrade', transport => { if (!stopped) setUploadChatTransport(transport.name); });
      deliveredIds.current.clear();
      clearTimeout(authRetry);
      setConnected(false);
      setMessengerAvailable(false); setFolders([]);
      setDialogs(() => ({}));
      setAssetMessages({});
      setAssetHistory({});
      void authenticate();
    });
    socket.on('call_control_packet', dispatchRemoteControl);
    for (const event of ['call_incoming', 'call_updated', 'call_ended']) {
      socket.on(event, (call: CallInfo) => callListeners.current.forEach(listener => listener(event, call)));
    }
    socket.on('chat_capabilities', (capabilities: { message_actions?: boolean; messenger_v2?: boolean; calls?: boolean }) => {
      setCallsAvailable(capabilities.calls === true);
      setMessageActionsAvailable(capabilities.message_actions === true);
  setMessengerAvailable(capabilities.messenger_v2 === true);
  if (capabilities.messenger_v2) socket.timeout(10000).emit('folders_get', {}, (err: Error | null, result?: { folders?: ChatFolder[] }) => { if (!err && result?.folders) setFolders(result.folders); });
    });
    socket.on('disconnect', () => { setUploadChatTransport(); recoverySince.current = Date.now(); setConnected(false); setMessageActionsAvailable(false); setCallsAvailable(false); });
    socket.on('connect_error', () => setConnected(false));
    socket.on('dialogs', (summaries: DialogSummaryWire[]) => {
      if (recoverySince.current !== null) {
        summaries.filter(summary => summary.updates_count > 0).forEach(summary => socket.emit('request_dialog_older_events', summary.dialog._id, new Date().toISOString(), () => undefined));
      }
      setDialogs((current) => {
        const next = { ...current };
        summaries.forEach((summary) => {
          next[summary.dialog._id] = decorateDialog(summary.dialog, {
            updated: summary.updated,
            updatesCount: summary.updates_count,
            events: summary.last_message ? [decorateEvent(summary.last_message)] : [],
          });
        });
        return next;
      });
    });
    socket.on('dialog_events', (dialogId: string, events: DialogEvent[]) => {
      if (recoverySince.current !== null) {
        const missed = events.map(decorateEvent).filter(event => event.type === 'message' && !event.seen && event.author_id !== user.id && eventDate(event) >= recoverySince.current! && !receivedLiveIds.current.has(event._id)).sort((a, b) => eventDate(a) - eventDate(b));
        if (missed.length) {
          missed.forEach(event => receivedLiveIds.current.add(event._id));
          for (const source of ['chat', 'task'] as const) {
            const group = missed.filter(event => messageNotificationSource(event) === source);
            const last = group.at(-1);
            if (last) void notify(last._id, 'Скрепка · сообщения после восстановления связи', group.length > 1 ? `Новых сообщений в диалоге: ${group.length}` : plainMessage(last.text) || 'Прикреплён файл', dialogId, source);
          }
        }
      }
      setDialogs((current) => {
        const dialog = current[dialogId];
        if (!dialog) return current;
        return {
          ...current,
          [dialogId]: {
            ...dialog,
            events: mergeEvents(dialog.events, events.map(decorateEvent)),
            loadingHistory: false,
          },
        };
      });
    });
    socket.on('dialog_event', (wireEvent: DialogEvent) => {
      const event = decorateEvent(wireEvent);
      const dialogId = event.dialog._id;
      const isIncomingMessage = event.type === 'message' && !event.seen && event.author_id !== user.id;
      const duplicate = receivedLiveIds.current.has(event._id);
      receivedLiveIds.current.add(event._id);
      if (receivedLiveIds.current.size > 1000) receivedLiveIds.current.delete(receivedLiveIds.current.values().next().value!);
      if (isIncomingMessage && !duplicate) {
        void notify(event._id, `Новое сообщение от ${event.dialog.system === 'files' ? 'Проводника' : usersRef.current[event.author_id]?.initial ?? 'пользователя'}`, plainMessage(event.text) || (event.files?.length ? 'Прикреплён файл' : 'Новое сообщение'), dialogId, messageNotificationSource(event));
      }
      setDialogs((current) => {
        const existing = current[dialogId];
        const dialog = existing ?? decorateDialog(event.dialog);
        const isUnread = !event.seen && event.author_id !== user.id;
        const updatedDialog = decorateDialog(event.dialog, {
          ...dialog,
          updated: event.created_at,
          updatesCount: Math.max(0, dialog.updatesCount + (isUnread && !duplicate && !dialog.events.some(item => item._id === event._id) ? 1 : 0)),
          events: mergeEvents(dialog.events, [event]),
        });
        if (event.type === 'leave' && event.author_id === user.id) {
          const next = { ...current };
          delete next[dialogId];
          return next;
        }
        return { ...current, [dialogId]: updatedDialog };
      });
    });
    socket.on('chat_folders', (items: ChatFolder[]) => setFolders(items));
socket.on('message_updated', (wire: DialogEvent) => {
  const event = decorateEvent(wire);
  setDialogs(current => {
    const d = current[event.dialog._id]; if (!d) return current;
    const previous = d.events.find(item => item._id === event._id);
    return { ...current, [d.id]: { ...d, events: mergeEvents(d.events, [event]), updatesCount: Math.max(0, d.updatesCount - (previous && !previous.seen && event.seen ? 1 : 0)) } };
  });
});
socket.on('messages_seen', ({ userId, ids }: { userId: number; ids: string[] }) => {
  const seenIds = new Set(ids);
  setDialogs(current => Object.fromEntries(Object.entries(current).map(([id, d]) => {
    const read = d.events.filter(e => seenIds.has(e._id) && !e.seen && userId === user.id).length;
    return [id, { ...d, updatesCount: Math.max(0, d.updatesCount - read), events: d.events.map(e => seenIds.has(e._id) ? { ...e, seen: userId === user.id ? true : e.seen, receivers: e.receivers.map(r => r.id === userId ? { ...r, seen: true } : r) } : e) }];
  })));
});
socket.on('message_removed', ({ dialogId, messageId }: { dialogId: string; messageId: string }) => {
  setDialogs(current => {
    const d = current[dialogId]; if (!d) return current;
    const removed = d.events.find(e => e._id === messageId);
    return { ...current, [dialogId]: { ...d, events: d.events.filter(e => e._id !== messageId), updatesCount: Math.max(0, d.updatesCount - (removed && !removed.seen ? 1 : 0)) } };
  });
  socket.emit('request_dialog_older_events', dialogId, new Date().toISOString(), () => undefined);
});
socket.on('asset_messages', (events: DialogEvent[]) => {
      const assetId = events[0]?.asset_id;
      if (assetId === undefined) return;
      setAssetMessages((current) => ({ ...current, [assetId]: mergeEvents([], events.map(decorateEvent)) }));
    });
    socket.on('asset_older_messages', (assetId: number, events: DialogEvent[]) => {
      setAssetMessages((current) => ({
        ...current,
        [assetId]: mergeEvents(current[assetId] ?? [], events.map(decorateEvent)),
      }));
      setAssetHistory((current) => ({
        ...current,
        [assetId]: { ...(current[assetId] ?? { exhausted: false }), loading: false },
      }));
    });
    socket.on('asset_message', (wireEvent: DialogEvent) => {
      if (wireEvent.asset_id === undefined) return;
      const event = decorateEvent(wireEvent);
      setAssetMessages((current) => ({
        ...current,
        [wireEvent.asset_id!]: mergeEvents(current[wireEvent.asset_id!] ?? [], [event]),
      }));
    });

    socket.connect();
    return () => {
      stopped = true;
      clearTimeout(authRetry);
      socket.removeAllListeners();
      socket.disconnect();
      setUploadChatTransport();
      socketRef.current = null;
      setConnected(false);
      setMessageActionsAvailable(false);
    };
  }, [decorateDialog, decorateEvent, notify, refresh, setDialogs, user?.id, user?.token]);

  useEffect(() => {
    if (!connected || !messengerAvailable) return;
    Object.values(dialogsById).forEach(d => d.events.forEach(e => {
      const recipient = e.receivers.find(r => r.id === user?.id);
      if (!deliveredIds.current.has(e._id) && e.type === 'message' && e.author_id !== user?.id && recipient && !recipient.delivered && !recipient.seen) {
        deliveredIds.current.add(e._id);
        socketRef.current?.timeout(10000).emit('message_received', { messageId: e._id }, (err:Error|null,ack?:ChatActionAck) => { if(err||!ack?.successful)deliveredIds.current.delete(e._id); });
      }
    }));
  }, [connected, messengerAvailable, dialogsById, user?.id]);

  const dialogs = useMemo(
    () => Object.values(dialogsById)
      .filter((dialog) => dialog.active !== false)
      .sort((a, b) => (b.updated ?? '').localeCompare(a.updated ?? '')),
    [dialogsById],
  );
  const directContactIds = useMemo(
    () => new Set(dialogs.filter((dialog) => dialog.direct).flatMap((dialog) => dialog.participants)),
    [dialogs],
  );
  const contacts = useMemo(
    () =>
      Object.values(usersById)
        .filter((item) => item.active && item.id !== user?.id && !directContactIds.has(item.id))
        .sort((a, b) => a.initial.localeCompare(b.initial, 'ru')),
    [directContactIds, user?.id, usersById],
  );

  const loadDialog = useCallback((dialogId: string) => {
    const dialog = dialogsRef.current[dialogId];
    if (!dialog || dialog.loaded || dialog.loadingHistory) return;
    setDialogs((current) => ({
      ...current,
      [dialogId]: { ...current[dialogId], loaded: true, loadingHistory: true },
    }));
    const anchor = dialog.events[0]?.created_at ?? new Date().toISOString();
    socketRef.current?.emit('request_dialog_older_events', dialogId, anchor, (ack: HistoryAck) => {
      setDialogs((current) => ({
        ...current,
        [dialogId]: {
          ...current[dialogId],
          loadingHistory: false,
          historyExhausted: ack.successful ? !ack.has_more : false,
        },
      }));
    });
  }, [setDialogs]);

  const loadOlderDialogEvents = useCallback((dialogId: string) => {
    const dialog = dialogsRef.current[dialogId];
    if (!dialog || dialog.loadingHistory || dialog.historyExhausted || !dialog.events.length) return;
    setDialogs((current) => ({ ...current, [dialogId]: { ...current[dialogId], loadingHistory: true } }));
    socketRef.current?.emit('request_dialog_older_events', dialogId, dialog.events[0].created_at, (ack: HistoryAck) => {
      setDialogs((current) => ({
        ...current,
        [dialogId]: {
          ...current[dialogId],
          loadingHistory: false,
          historyExhausted: ack.successful ? !ack.has_more : false,
        },
      }));
    });
  }, [setDialogs]);

  const markSeen = useCallback((dialogId: string, eventId: string) => {
    const dialog = dialogsRef.current[dialogId];
    const event = dialog?.events.find((item) => item._id === eventId);
    if (!dialog || !event || event.seen) return;
    setDialogs((current) => ({
      ...current,
      [dialogId]: {
        ...current[dialogId],
        updatesCount: Math.max(0, current[dialogId].updatesCount - 1),
        events: current[dialogId].events.map((item) => item._id === eventId ? { ...item, seen: true } : item),
      },
    }));
    socketRef.current?.timeout(10000).emit('set_seen', eventId, (error: Error | null, successful?: boolean) => {
      if (!error && successful) return;
      setDialogs((current) => ({
        ...current,
        [dialogId]: {
          ...current[dialogId],
          updatesCount: current[dialogId].updatesCount + 1,
          events: current[dialogId].events.map((item) => item._id === eventId ? { ...item, seen: false } : item),
        },
      }));
    });
  }, [setDialogs]);

  const markAllSeen = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket?.connected) throw new Error('Нет соединения с чатом. Попробуйте после подключения.');
    const snapshots = Object.fromEntries(Object.entries(dialogsRef.current).map(([dialogId, dialog]) => [dialogId, {
      updatesCount: dialog.updatesCount,
      eventIds: dialog.events.filter((event) => !event.seen).map((event) => event._id),
    }]));
    setDialogs((current) => Object.fromEntries(Object.entries(current).map(([dialogId, dialog]) => [dialogId, {
      ...dialog,
      updatesCount: 0,
      events: dialog.events.map((event) => ({ ...event, seen: true })),
    }])));
    try {
      await new Promise<void>((resolve, reject) => {
        socket.timeout(15000).emit('set_all_seen', (error: Error | null, successful?: boolean) => {
          if (error || !successful) reject(new Error('Не удалось отметить сообщения прочитанными. Попробуйте ещё раз.'));
          else resolve();
        });
      });
    } catch (error) {
      setDialogs((current) => Object.fromEntries(Object.entries(current).map(([dialogId, dialog]) => {
        const snapshot = snapshots[dialogId];
        if (!snapshot) return [dialogId, dialog];
        const eventIds = new Set(snapshot.eventIds);
        return [dialogId, {
          ...dialog,
          updatesCount: dialog.updatesCount + snapshot.updatesCount,
          events: dialog.events.map((event) => eventIds.has(event._id) ? { ...event, seen: false } : event),
        }];
      })));
      throw error;
    }
  }, [setDialogs]);

  const requestMessageAction = useCallback((event: string, payload: unknown) => new Promise<ChatActionAck>((resolve, reject) => {
    const socket = socketRef.current;
    if (!socket?.connected || !messageActionsAvailable) {
      reject(new Error('Ответы и пересылка пока недоступны. Дождитесь подключения чата.'));
      return;
    }
    socket.timeout(20000).emit(event, payload, (error: Error | null, result?: ChatActionAck) => {
      if (error) reject(new Error('Нет подтверждения отправки. Проверьте последние сообщения перед повторной отправкой.'));
      else if (!result?.successful) reject(new Error(result?.error || 'Не удалось отправить сообщение.'));
      else resolve(result);
    });
  }), [messageActionsAvailable]);

  const joinAsset = useCallback((assetId: number) => {
    setAssetHistory((current) => ({ ...current, [assetId]: { loading: true, exhausted: false } }));
    socketRef.current?.emit('join_asset_room', assetId, (ack: HistoryAck) => {
      setAssetHistory((current) => ({
        ...current,
        [assetId]: {
          loading: false,
          exhausted: ack.successful ? !ack.has_more : false,
        },
      }));
    });
  }, []);
  const leaveAsset = useCallback((assetId: number) => {
    socketRef.current?.emit('leave_asset_room', assetId);
  }, []);
  const loadOlderAssetMessages = useCallback((assetId: number) => {
    const messages = assetMessages[assetId];
    const history = assetHistory[assetId];
    if (messages?.length && !history?.loading && !history?.exhausted) {
      setAssetHistory((current) => ({
        ...current,
        [assetId]: { ...(current[assetId] ?? { exhausted: false }), loading: true },
      }));
      socketRef.current?.emit('request_asset_older_messages', assetId, messages[0].created_at, (ack: HistoryAck) => {
        setAssetHistory((current) => ({
          ...current,
          [assetId]: {
            loading: false,
            exhausted: ack.successful ? !ack.has_more : false,
          },
        }));
      });
    }
  }, [assetHistory, assetMessages]);

  const value = useMemo<Omit<ChatContextValue, 'callsAvailable' | 'callRequest' | 'subscribeCalls'>>(() => ({
    connected,
messengerAvailable, folders,
messengerAction: (name, payload) => {
  if (!messengerAvailable) return Promise.reject(new Error('Для этого действия требуется обновление сервера.'));
  return requestMessageAction(name, payload);
},
messageActionsAvailable,
    contacts,
    dialogs,
    usersById,
    unreadCount: dialogs.reduce((sum, dialog) => sum + Math.max(0, dialog.updatesCount), 0),
    assetMessages,
    assetHistory,
    notificationPermission,
    enableNotifications,
    notificationReport, testNotificationSound, testNotificationPopup, notificationSoundVolume, notificationSoundMuted, setNotificationSoundVolume, toggleNotificationSound,
    taskNotifications: {sound:taskSound,popup:taskPopup}, setTaskNotificationEnabled,
    createDialog: (title, participants) => socketRef.current?.emit('create_dialog', title, participants),
    invite: (dialogId, participants) => socketRef.current?.emit('invite_to_dialog', dialogId, participants),
    leave: (dialogId) => socketRef.current?.emit('leave_from_dialog', dialogId),
    rename: (dialogId, title) => socketRef.current?.emit('rename_dialog', dialogId, title),
    loadDialog,
    loadOlderDialogEvents,
    markSeen,
    markAllSeen,
    replyToDialog: (dialogId, replyToId, text, assetId, files) => requestMessageAction('reply_message', { dialogId, replyToId, text, assetId, files: filesForWire(files) }),
    forwardMessage: (messageId, target) => requestMessageAction('forward_message', { messageId, target }),
    sendToDialog: (dialogId, text, assetId, files) =>
      socketRef.current?.emit('send_message_to_dialog', text, dialogId, assetId, filesForWire(files)),
    sendDirect: (userId, text, assetId, files) =>
      socketRef.current?.emit('send_direct_message', text, userId, assetId, filesForWire(files)),
    joinAsset,
    leaveAsset,
    loadOlderAssetMessages,
  }), [messengerAvailable, folders, assetHistory, assetMessages, connected, contacts, dialogs, enableNotifications, joinAsset, leaveAsset, loadDialog, loadOlderAssetMessages, loadOlderDialogEvents, markAllSeen, markSeen, messageActionsAvailable, notificationPermission, notificationReport, testNotificationSound, testNotificationPopup, notificationSoundVolume, notificationSoundMuted, setNotificationSoundVolume, toggleNotificationSound, taskSound, taskPopup, setTaskNotificationEnabled, requestMessageAction, usersById]);

  return <ChatContext value={{ ...value, callsAvailable, callRequest, subscribeCalls }}>{children}</ChatContext>;
}

export function useChat(): ChatContextValue {
  const value = use(ChatContext);
  if (!value) throw new Error('useChat must be used inside ChatProvider');
  return value;
}
